import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { READ_ONLY_EVENT, exitViewAs, isExecutive } from '../utils/viewAs';

const NOTICE_MS = 6000;
const OFFSET = '--view-as-offset';

/*
 * While an administrator views the app as another role: a bar across the top
 * of every page, the Field camera included, saying which role and that
 * nothing can be changed, with Exit. An executive's account (0098), which may
 * only read, gets the same bar without Exit. A write the server refused for
 * that reason (api.js, the Field store, the sync queue) is said here too, for
 * a moment, wherever on the page it happened.
 *
 * It is fixed, so it publishes its height as --view-as-offset on <html>:
 * the body is pushed down by it (index.css) and the full-screen camera starts
 * below it. Nothing is rendered, and the variable is removed, otherwise.
 */
export default function ViewAsBanner({ navigate = (path) => window.location.assign(path) }) {
  const { viewAs, user } = useAuth();
  const { t } = useTranslation(['viewAs', 'users']);
  const [notice, setNotice] = useState(false);
  const ref = useRef(null);
  const executive = !viewAs && isExecutive(user);
  const role = viewAs?.role || (executive ? user.roles[0] : null);

  useEffect(() => {
    if (!role) return undefined;
    let timer = null;
    const onRefused = () => {
      setNotice(true);
      clearTimeout(timer);
      timer = setTimeout(() => setNotice(false), NOTICE_MS);
    };
    window.addEventListener(READ_ONLY_EVENT, onRefused);
    return () => {
      window.removeEventListener(READ_ONLY_EVENT, onRefused);
      clearTimeout(timer);
    };
  }, [role]);

  useLayoutEffect(() => {
    const root = document.documentElement;
    const node = ref.current;
    if (!role || !node) {
      root.style.removeProperty(OFFSET);
      return undefined;
    }
    const publish = () => root.style.setProperty(OFFSET, `${Math.ceil(node.getBoundingClientRect().height)}px`);
    publish();
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(publish) : null;
    observer?.observe(node);
    return () => {
      observer?.disconnect();
      root.style.removeProperty(OFFSET);
    };
  }, [role]);

  if (!role) return null;

  const exit = () => {
    exitViewAs();
    navigate('/');
  };

  return (
    <div ref={ref} role="region" aria-label={t(executive ? 'banner.executiveLabel' : 'banner.label')}
      data-testid={executive ? 'read-only-banner' : 'view-as-banner'}
      style={{
        position: 'fixed', top: 0, insetInline: 0, zIndex: 4000, whiteSpace: 'normal',
        display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '4px 12px',
        padding: 'calc(6px + env(safe-area-inset-top, 0px)) 16px 6px',
        background: 'var(--c-warning-soft, #fff3cd)', color: 'var(--c-warning-text-fg, #5c4400)',
        borderBottom: '1px solid var(--c-warning, #c99a00)', fontSize: 14, boxSizing: 'border-box',
      }}>
      {executive ? <strong>{t('banner.executive')}</strong> : (<>
        <strong>{t('banner.viewing', { role: t(`users:role.${role}`, { defaultValue: role }) })}</strong>
        <span>{t('banner.readOnly')}</span>
        <span style={{ fontSize: 12, flexBasis: '100%', order: 3 }}>{t('banner.scopeNote')}</span>
      </>)}
      <span role="status" aria-live="polite" data-testid="view-as-refused"
        style={{ fontWeight: 600, order: 2 }}>
        {notice ? t('banner.refused') : ''}
      </span>
      {!executive && <button type="button" onClick={exit} data-testid="view-as-exit"
        style={{ marginInlineStart: 'auto', order: 2, background: 'var(--c-surface)', color: 'var(--c-text-fg)',
          border: '1px solid var(--c-warning, #c99a00)', borderRadius: 6, padding: '6px 14px', minHeight: 36,
          fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
        {t('banner.exit')}
      </button>}
    </div>
  );
}
